import cn from 'classnames';
import { useState, useEffect, useMemo, useCallback } from 'react';

import {
  SetArticleQuantityBody,
  SetArticleQuantityBodyV2,
  useCheckoutContext,
} from '@zooplus/checkout-state';
import { P2, FlatBox, Switch, AutoshipmentLogoLockup } from '@zooplus/ui-components';

import { getAppConfig } from '@config/get-app-config';
import useCartTracking from '@hooks/use-adobe-tracking/use-cart-tracking';
import { useTranslation } from '@hooks/use-translations';
import { CART_VERSION, CHECKOUT_STATE_PROJECT_ID } from '@utilities/constants';

import styles from './cart-subscription-toggle.module.scss';

type CartSubscriptionToggleProps = {
  autoshipmentDetails?: { percentage?: number | null };
  autoshipmentApplied?: boolean;
  articleQuantity: number;
  id: number;
  shopIdentifier: string;
  subtotal: number;
  actualPrice: number;
  abdApplied?: boolean;
  abdOfferId?: string | undefined;
  offerId?: string;
  ausOfferId?: string;
};

const CartSubscriptionToggle = ({
  autoshipmentApplied,
  autoshipmentDetails,
  articleQuantity,
  id,
  shopIdentifier,
  subtotal,
  actualPrice,
  abdApplied,
  abdOfferId,
  offerId,
  ausOfferId,
}: CartSubscriptionToggleProps) => {
  const { setArticleQuantity, cartVersion } = useCheckoutContext(CHECKOUT_STATE_PROJECT_ID);
  const {
    site: { realm, locale },
  } = getAppConfig();
  const { trackAboToggle } = useCartTracking();
  const { t } = useTranslation();

  const [isAutoshipmentApplied, setIsSubscriptionApplied] = useState(autoshipmentApplied);

  useEffect(() => {
    setIsSubscriptionApplied(autoshipmentApplied);
  }, [autoshipmentApplied]);

  const handleToggleChange = useCallback(() => {
    const subscriptionAppliedValue = !isAutoshipmentApplied;
    setIsSubscriptionApplied(subscriptionAppliedValue);
    const isCartApiV2: boolean = cartVersion === CART_VERSION.V2;
    const setArticleQuantityPayload: SetArticleQuantityBody = {
      articleId: id,
      quantity: articleQuantity,
    };
    if (!isCartApiV2) {
      setArticleQuantityPayload.subscriptionSelected = subscriptionAppliedValue;
      setArticleQuantityPayload.voucherApplied = abdApplied;
    } else {
      if (abdApplied && abdOfferId) {
        (setArticleQuantityPayload as SetArticleQuantityBodyV2).offerIds = [
          ausOfferId as string,
          abdOfferId as string,
        ];
      } else {
        (setArticleQuantityPayload as SetArticleQuantityBodyV2).offerIds = [
          (subscriptionAppliedValue ? ausOfferId : offerId) as string,
        ];
      }
    }
    setArticleQuantity?.(setArticleQuantityPayload);
  }, [
    isAutoshipmentApplied,
    cartVersion,
    id,
    articleQuantity,
    setArticleQuantity,
    abdApplied,
    abdOfferId,
    ausOfferId,
    offerId,
  ]);

  const toggleClass = useMemo(() => {
    const styleMap: { [key: string]: string } = {
      subscriptionWrapper: cn(
        styles.subscriptionWrapper,
        isAutoshipmentApplied && styles.subscriptionWrapper_applied,
      ),
      percentage: cn(
        styles.percentage,
        isAutoshipmentApplied && styles.percentage_subscriptionApplied,
      ),
    };

    return (element: string) => styleMap[element] || undefined;
  }, [isAutoshipmentApplied]);

  useEffect(() => {
    isAutoshipmentApplied !== autoshipmentApplied &&
      trackAboToggle(
        id,
        shopIdentifier,
        articleQuantity,
        actualPrice,
        subtotal,
        !!isAutoshipmentApplied,
      );
  }, [isAutoshipmentApplied, autoshipmentApplied]);

  return (
    <section data-testid="cart-subscription-toggle" className={styles.wrapper}>
      <FlatBox rounded variant="white" className={toggleClass('subscriptionWrapper')}>
        <Switch
          data-testid="zooplus-Abo"
          isPressed={!!isAutoshipmentApplied}
          onClick={handleToggleChange}
          aria-label={t('cart.overview.autoshipment.toggle')}
        />
        <div data-testid="subscription-logo" className={cn(styles.zooplusAbo)}>
          <AutoshipmentLogoLockup locale={locale} realm={realm} />
        </div>
        <div className={styles.toggleWrapper}>
          {autoshipmentDetails?.percentage && autoshipmentDetails?.percentage > 0 && (
            <div className={toggleClass('percentage')} data-testid="subscription-discount-value">
              <P2 className={styles.percentageValue}>{`-${autoshipmentDetails?.percentage}%`}</P2>
            </div>
          )}
        </div>
      </FlatBox>
    </section>
  );
};

export default CartSubscriptionToggle;
